import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

import { queryClient } from '~app/query-client';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface ApproveManualPaymentParams {
  invoiceId: Array<string>;
  date: string;
}

interface ApproveManualPaymentResult {
  requested: number;
  found: number;
  processed: number;
  skipped: Array<{ invoiceId: string; reason: string }>;
  failed: Array<{ invoiceId: string; reason: string }>;
}

const invalidatePaymentQueries = async (date: string) => {
  // Keep invalidation focused — broad AR/dashboard refetches flicker the whole
  // Payment Processing surface after each Retry (ENR-2160).
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [KEYS.PAYMENT_DETAILS, date],
    }),
    queryClient.invalidateQueries({
      queryKey: [KEYS.PAYMENT_HISTORY],
    }),
    queryClient.invalidateQueries({
      queryKey: [KEYS.STUDENT_PAYMENT_HISTORY],
    }),
  ]);
};

const scheduleFollowUpRefresh = (date: string) => {
  // Single delayed refresh once Stripe may have confirmed the charge.
  window.setTimeout(() => {
    void queryClient.invalidateQueries({
      queryKey: [KEYS.PAYMENT_DETAILS, date],
    });
    void queryClient.invalidateQueries({
      queryKey: [KEYS.PAYMENT_HISTORY],
    });
    void queryClient.invalidateQueries({
      queryKey: [KEYS.STUDENT_PAYMENT_HISTORY],
    });
  }, 4000);
};

export const approveManualPayment = async ({
  invoiceId,
}: ApproveManualPaymentParams) => {
  const response = await baseAPI.post<ApproveManualPaymentResult>(
    'subscription-invoices/approve-manual-payments',
    {
      invoiceId,
      // Studio staff explicitly clicking Collect Payment / Reattempt Charge
      // is the approval to charge before the invoice due date (wallet + card).
      allowEarlyCollection: true,
    },
  );
  return response.data;
};

export const useApproveManualPayments = () => {
  return useMutation({
    mutationFn: (params: ApproveManualPaymentParams) =>
      approveManualPayment(params),

    onSuccess: async (result, params) => {
      await invalidatePaymentQueries(params.date);
      scheduleFollowUpRefresh(params.date);

      if (result.processed > 0) {
        toast.success(
          'Payment reattempt started. Status may stay pending until the payment provider confirms.',
        );
      } else if (result.skipped.length > 0 && result.failed.length === 0) {
        toast.warning(
          result.skipped[0]?.reason ?? 'Invoice could not be retried',
        );
      } else if (result.failed.length > 0) {
        toast.error(result.failed[0]?.reason ?? 'Failed to reattempt payment');
      } else {
        toast.warning('No invoice was processed');
      }
    },

    onError: (error: AxiosError<{ message: string }>) => {
      console.error('Error approving payments:', error);

      // Extract the actual error message from the response
      let errorMessage = 'Failed to approve payments';

      if (error.response?.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message && !error.message.includes('status code')) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    },
  });
};
